/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  getDatabase,
  ref,
  set,
  update,
  onDisconnect,
  remove,
  serverTimestamp,
} from "firebase/database";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { logUserAction } from "@/utils/errorLogger";
import { watchPositionSafe, type WatchHandle } from "@/utils/geolocation";
import { buildTrackLink } from "@/utils/walkMeHome/trackLink";
import {
  isWithinArrival,
  LOCATION_CADENCE_MS,
  recomputeEtaMinutes,
  buildStartWalkSms,
  buildMarkSafeSms,
  type WalkSessionWatcher,
} from "@/utils/walkMeHome/sessionLifecycle";
import {
  sendWalkMeHomeSms,
  summarizeSmsOutcome,
} from "@/utils/walkMeHome/smsClient";
import {
  type TravelMode,
  DEFAULT_TRAVEL_MODE,
  normalizeTravelMode,
} from "@/utils/walkMeHome/travelMode";
import { fetchWmhRoute } from "@/utils/walkMeHome/directionsClient";
import {
  startWmhLiveActivity,
  updateWmhLiveActivity,
  endWmhLiveActivity,
  onWmhPushToken,
} from "@/utils/walkMeHome/liveActivity";
import { straightLinePercentRemaining } from "@/utils/walkMeHome/routeProgress";
import { requestAlwaysLocation } from "@/utils/locationAlways";
import {
  hideEtaNotification,
  showEtaNotification,
} from "@/utils/walkMeHome/persistentNotification";
import {
  recordLocationWatchError,
  recordRtdbWriteFailed,
  recordStartedWithoutWatchers,
  type WmhInstrumentationSink,
} from "@/utils/safecircle/wmhInstrumentation";

const NOTIFICATION_REFRESH_MS = 60_000;

// Sentry adapter for the pure WMH instrumentation helpers (System A). Module-
// level: stable identity, matches the sentrySink pattern in useSOSEvent.
const wmhSink: WmhInstrumentationSink = {
  addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
  captureMessage: (message, ctx) => Sentry.captureMessage(message, ctx),
  captureException: (err, ctx) => Sentry.captureException(err, ctx),
};

interface Destination {
  lat: number;
  lng: number;
  name: string;
}

export interface StartWalkOptions {
  watchers: WalkSessionWatcher[];
  /** Walker's E.164 phone, captured at start so backend delay alerts can SMS
   *  the walker for confirmation. May be empty if the user never verified. */
  walkerPhone: string;
  /** Travel mode — "walk" (default) or "drive" (car / rideshare). Governs the
   *  ETA pace, the copy, and is persisted so the tracker + SMS read correctly.
   *  Optional for back-compat; omitted → "walk". */
  mode?: TravelMode;
}

interface WalkMeHomeState {
  active: boolean;
  destination: Destination | null;
  startTime: number | null;
  etaMinutes: number | null;
  expectedArrivalMs: number | null;
  sessionId: string | null;
  currentLat: number;
  currentLng: number;
  watchers: WalkSessionWatcher[];
  /** Travel mode of the active session — drives the overlay's icon + copy. */
  travelMode: TravelMode;
  /** Origin captured on the first location fix — the denominator for the
   *  walker card's distance-based % remaining. 0 until the first fix. */
  originLat: number;
  originLng: number;
}

interface WalkMeHomeContextType extends WalkMeHomeState {
  startWalk: (
    destination: Destination,
    etaMinutes: number,
    options: StartWalkOptions,
  ) => Promise<void>;
  markSafe: () => Promise<void>;
  cancelWalk: () => void;
  remainingSeconds: number;
  isOverdue: boolean;
  shareLink: string | null;
}

const WalkMeHomeContext = createContext<WalkMeHomeContextType | null>(null);

export function useWalkMeHome() {
  const ctx = useContext(WalkMeHomeContext);
  if (!ctx)
    throw new Error("useWalkMeHome must be used within WalkMeHomeProvider");
  return ctx;
}

// PR #56 / pre-11b countdown buffer — kept under Option 1 (see
// docs/port-wmh-11b.md §1c) so the existing pill-goes-red path is preserved.
const BUFFER_MINUTES = 5;

function generateSessionId() {
  return `wmh_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function watchersToRtdb(
  watchers: WalkSessionWatcher[],
): Record<string, WalkSessionWatcher & { id: string }> {
  const out: Record<string, WalkSessionWatcher & { id: string }> = {};
  watchers.forEach((w, idx) => {
    const id = `w_${idx}_${w.phone.replace(/\D/g, "").slice(-4)}`;
    out[id] = { ...w, id };
  });
  return out;
}

export function WalkMeHomeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<WalkMeHomeState>({
    active: false,
    destination: null,
    startTime: null,
    etaMinutes: null,
    expectedArrivalMs: null,
    sessionId: null,
    currentLat: 0,
    currentLng: 0,
    watchers: [],
    travelMode: DEFAULT_TRAVEL_MODE,
    originLat: 0,
    originLng: 0,
  });
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isOverdue, setIsOverdue] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  // Mirror of isOverdue for the interval-driven Live Activity update closure
  // (avoids a stale-closure read of the state value inside setInterval).
  const isOverdueRef = useRef(false);
  isOverdueRef.current = isOverdue;
  const locationWatchRef = useRef<WatchHandle | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firebaseIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  // Live Activity APNs push-token listener handle (removed on walk end).
  const pushTokenListenerRef = useRef<{ remove: () => void } | null>(null);
  const notifIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentLocRef = useRef({ lat: 0, lng: 0 });
  // Guards the one-time origin persist + route fetch: done on the FIRST location
  // fix of a session (origin isn't known until then), then never again.
  const originCapturedRef = useRef(false);
  // Latest state for the location push interval and the auto-arrival effect
  // so they don't capture stale closures.
  const stateRef = useRef<WalkMeHomeState>(state);
  stateRef.current = state;
  // markArrivedRef is set after markSafe is declared (forward reference) so
  // the auto-arrival useEffect can call it without depending on markSafe's
  // identity (which would re-bind every render).
  const markArrivedRef = useRef<() => Promise<void>>(async () => {});

  // Push location to Firebase RTDB. Uses update() so that fields written once
  // at session start (createdAt, userName, destination*) aren't clobbered by
  // the periodic location pushes.
  const pushLocation = useCallback((lat: number, lng: number) => {
    const sid = stateRef.current.sessionId;
    if (!sid) return;
    try {
      const db = getDatabase();
      const sessionDbRef = ref(db, `walkMeHome/${sid}`);
      update(sessionDbRef, {
        lat,
        lng,
        updatedAt: Date.now(),
        active: true,
      });
    } catch (e) {
      console.warn("Firebase push failed:", e);
      // Every location push can fail unlogged — a watcher then sees a frozen
      // dot with no signal to anyone. Capture so a persistently-failing push
      // (rules/network) is visible. Non-fatal: the walk keeps running.
      recordRtdbWriteFailed(wmhSink, e, {
        system: "A",
        phase: "location_push",
        sessionId: sid,
      });
    }
  }, []);

  // Start walk
  const startWalk = useCallback(
    async (
      destination: Destination,
      etaMinutes: number,
      options: StartWalkOptions,
    ) => {
      // Request notification permission for auto-alerts (still used by
      // main's triggerOverdueAlert path — see below).
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }

      const sessionId = generateSessionId();
      const now = Date.now();
      const expectedArrivalMs = now + etaMinutes * 60_000;
      const walkerName = user?.displayName || user?.email || "Someone";
      const travelMode = normalizeTravelMode(options.mode);

      setState({
        active: true,
        destination,
        startTime: now,
        etaMinutes,
        expectedArrivalMs,
        sessionId,
        currentLat: 0,
        currentLng: 0,
        watchers: options.watchers,
        travelMode,
        originLat: 0,
        originLng: 0,
      });
      setIsOverdue(false);
      setAlertSent(false);
      originCapturedRef.current = false;

      // Persist the session shell once so Safety First (and any other badge
      // logic) can read createdAt. The new fields (walkerUid, walkerPhone,
      // etaMinutes, startedAt, expectedArrivalMs, status, watchers) are
      // additive — written once here so the future Phase 11d scheduled
      // function can read them. Subsequent location pushes use update() and
      // won't clobber these fields.
      try {
        const db = getDatabase();
        const sessionDbRef = ref(db, `walkMeHome/${sessionId}`);
        const watchersMap = watchersToRtdb(options.watchers);
        await set(sessionDbRef, {
          sessionId,
          walkerUid: user?.uid ?? "",
          walkerName,
          // Main's legacy field — kept for Safety First / TrackingPage
          // compatibility.
          userName: walkerName,
          walkerPhone: options.walkerPhone || "",
          destinationName: destination.name,
          destinationLat: destination.lat,
          destinationLng: destination.lng,
          // Travel mode drives the tracker/SMS copy ("walking" vs "driving")
          // and, in a later change, mode-aware route drawing. Legacy sessions
          // without this field read as "walk" via normalizeTravelMode.
          travelMode,
          etaMinutes,
          startedAt: now,
          expectedArrivalMs,
          status: "active",
          createdAt: serverTimestamp(),
          updatedAt: now,
          active: true,
          watchers: watchersMap,
        });
        // Mark inactive on client disconnect so the watcher dashboard stays
        // accurate. Moved to set-time so we don't re-register on each push.
        onDisconnect(sessionDbRef).update({
          active: false,
          disconnectedAt: Date.now(),
        });
      } catch (e) {
        console.warn("Firebase session init failed:", e);
        // The canonical session record failed to persist — watchers' tracking
        // page has nothing to read, yet the local walk proceeds as if saved.
        // Capture so this silent divergence is visible.
        recordRtdbWriteFailed(wmhSink, e, {
          system: "A",
          phase: "session_init",
          sessionId,
        });
      }

      logUserAction("walk-me-home-started", {
        destination: destination.name,
        etaMinutes,
        watcherCount: options.watchers.length,
      });

      // Escalate to Always location so the walker's position keeps flowing
      // after the screen locks (Batch D P0-18b). The native plugin is iOS-
      // only; web / Android resolve as 'unsupported' and we move on.
      void requestAlwaysLocation().then((outcome) => {
        Sentry.addBreadcrumb({
          category: "walk-me-home",
          message: "always_location_outcome",
          level: outcome.status === "authorized_always" ? "info" : "warning",
          data: {
            sessionId,
            status: outcome.status,
            changed: outcome.changed,
            needsSettings:
              outcome.status === "denied" || outcome.status === "restricted"
                ? true
                : undefined,
          },
        });
      });

      // Persistent notification fallback for lock-screen ETA visibility
      // (Batch D P0-18a — placeholder until native Live Activity ships in
      // Build 92). Fire-and-forget; permission failures are recorded in
      // breadcrumbs but don't block the walk.
      void showEtaNotification({
        destinationName: destination.name,
        etaMinutes,
        startedAt: now,
      }).then((outcome) => {
        Sentry.addBreadcrumb({
          category: "walk-me-home",
          message: "notification_outcome",
          level: outcome === "scheduled" ? "info" : "warning",
          data: { sessionId, outcome },
        });
      });

      // Start the iOS Live Activity (lock screen + Dynamic Island). iOS 16.2+
      // only; no-ops elsewhere, so the persistent notification above stays the
      // fallback. Fire-and-forget — never blocks the walk. percentComplete
      // starts at 0 (full "% left").
      void startWmhLiveActivity({
        walkerName,
        destinationName: destination.name,
        travelMode,
        percentComplete: 0,
        etaMinutes,
      }).then((started) => {
        Sentry.addBreadcrumb({
          category: "walk-me-home",
          message: "live_activity_start",
          level: started ? "info" : "info",
          data: { sessionId, started },
        });
      });

      // Capture the Live Activity's APNs push token and write it under the
      // session so the server push function can update the card while the app is
      // suspended/locked (the lock-screen-freeze fix). Token may rotate; each
      // emission overwrites. iOS-only; onWmhPushToken is a no-op elsewhere.
      void onWmhPushToken((role, token) => {
        if (role !== "walker") return;
        try {
          const db = getDatabase();
          // Stored OUTSIDE the publicly-readable walkMeHome/{sessionId} node
          // (that node is public-read for the tracker link) so the push token
          // isn't exposed to anyone with the link. Auth-only path.
          void update(ref(db, `walkMeHomePushTokens/${sessionId}`), {
            walker: token,
          });
        } catch (e) {
          console.warn("[walkMeHome] push-token persist failed:", e);
        }
      }).then((handle) => {
        pushTokenListenerRef.current = handle;
      });

      // Start watching location via the unified Capacitor wrapper so iOS uses
      // the native plugin (single permission prompt) instead of the WKWebView
      // navigator.geolocation API (extra "localhost wants to know" prompt).
      locationWatchRef.current = watchPositionSafe(
        (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          currentLocRef.current = { lat, lng };
          setState((s) => ({ ...s, currentLat: lat, currentLng: lng }));

          // On the FIRST fix: persist the origin (typed on the session shape but
          // previously never written — the tracker needs it to draw the route)
          // and fetch the road route ONCE for the map polyline + a road-accurate
          // ETA. Both are fire-and-forget: a failure leaves the tracker on its
          // straight-line fallback, never blocking the walk.
          if (!originCapturedRef.current) {
            originCapturedRef.current = true;
            // Expose origin in state so the overlay card can compute
            // distance-based % remaining (dist(current→dest)/dist(origin→dest)).
            setState((s) => ({ ...s, originLat: lat, originLng: lng }));
            try {
              const db = getDatabase();
              void update(ref(db, `walkMeHome/${sessionId}`), {
                originLat: lat,
                originLng: lng,
              });
            } catch (e) {
              console.warn("[walkMeHome] origin persist failed:", e);
            }
            void fetchWmhRoute({
              originLat: lat,
              originLng: lng,
              destLat: destination.lat,
              destLng: destination.lng,
              mode: travelMode,
            }).then((route) => {
              if (!route?.polyline) return;
              try {
                const db = getDatabase();
                void update(ref(db, `walkMeHome/${sessionId}`), {
                  routePolyline: route.polyline,
                  routeDistanceMeters: route.distanceMeters ?? null,
                  routeDurationSeconds: route.durationSeconds ?? null,
                });
              } catch (e) {
                console.warn("[walkMeHome] route persist failed:", e);
              }
            });
          }
        },
        (err) => {
          // P0 silent point: this was an empty () => {}. A GPS/permission
          // failure here means location never starts, currentLoc stays 0,0,
          // the push interval's `if (lat && lng)` guard never fires, and the
          // walk looks active but is invisible to watchers. Capture it. We do
          // NOT change UX here (logging-only phase).
          recordLocationWatchError(wmhSink, err, { system: "A", sessionId });
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );

      // Fire the start-walk SMS fan-out to watchers. We await the outcome so
      // the walker sees a toast if it failed (Question C — TWILIO env vars
      // unset or any non-ok response means the safety net didn't activate
      // and the walker needs to know).
      if (options.watchers.length > 0) {
        const trackLink = buildTrackLink(sessionId);
        const startBody = buildStartWalkSms({
          walkerName,
          destinationName: destination.name,
          etaMinutes,
          trackLink,
          mode: travelMode,
        });
        try {
          const outcome = await sendWalkMeHomeSms({
            to: options.watchers.map((w) => w.phone),
            body: startBody,
            getIdToken: () => user?.getIdToken() ?? Promise.resolve(null),
          });
          Sentry.addBreadcrumb({
            category: "walk-me-home",
            message: "start_sms_outcome",
            level: outcome.ok ? "info" : "warning",
            data: {
              sessionId,
              sent: outcome.sent,
              failed: outcome.failed,
              summary: summarizeSmsOutcome(outcome),
            },
          });
          if (!outcome.ok) {
            toast.error(
              `Couldn't notify your contacts (${outcome.sent}/${outcome.sent + outcome.failed} sent). Your walk is being tracked but the SMS didn't go out.`,
            );
          }
        } catch (err) {
          console.error("[walkMeHome] start SMS failed:", err);
          Sentry.captureException(err, {
            tags: { area: "walk-me-home", phase: "start-sms" },
          });
          toast.error(
            "Couldn't notify your contacts. Your walk is being tracked but the SMS didn't go out.",
          );
        }
      } else {
        // Zero watchers → the start-walk SMS safety net is skipped entirely,
        // with no signal today. Breadcrumb it (may be an intentional solo walk).
        recordStartedWithoutWatchers(wmhSink, sessionId);
      }
    },
    [user],
  );

  // Firebase sync interval (cadence from 11a sessionLifecycle —
  // LOCATION_CADENCE_MS = 12s; pre-11b main used 30s). Only restarts when
  // the session starts/stops. onDisconnect is now registered at set-time
  // inside startWalk, so we don't re-register it on every restart.
  useEffect(() => {
    if (!state.active || !state.sessionId) return;

    const push = () => {
      const { lat, lng } = currentLocRef.current;
      if (lat && lng) pushLocation(lat, lng);
    };

    push(); // Initial push

    firebaseIntervalRef.current = setInterval(push, LOCATION_CADENCE_MS);

    return () => {
      if (firebaseIntervalRef.current)
        clearInterval(firebaseIntervalRef.current);
    };
  }, [state.active, state.sessionId, pushLocation]);

  // Refresh the persistent notification each minute with a dynamic ETA. Uses
  // the haversine recompute against current GPS so the lock-screen card
  // doesn't drift from reality. Skipped when no destination is known yet.
  // Separate from the 1s countdown timer (PR #56) and the Option 1 overdue
  // path (PR #63) — this useEffect only touches the Capacitor notification.
  useEffect(() => {
    if (!state.active || !state.destination) return;
    const refresh = () => {
      const { lat, lng } = currentLocRef.current;
      const dest = stateRef.current.destination;
      if (!dest) return;
      const startedAt = stateRef.current.startTime ?? Date.now();
      const dynamicEta =
        lat && lng
          ? recomputeEtaMinutes(lat, lng, dest.lat, dest.lng)
          : (stateRef.current.etaMinutes ?? 0);
      void showEtaNotification({
        destinationName: dest.name,
        etaMinutes: dynamicEta,
        startedAt,
      });

      // Update the Live Activity with the same cadence. percentComplete =
      // 100 − distance-based % remaining (the widget fills the bar with covered
      // distance while showing "% left"). Overdue reflected in status.
      const s = stateRef.current;
      const remaining =
        lat && lng && s.originLat && s.originLng
          ? straightLinePercentRemaining(
              { lat, lng },
              { lat: s.originLat, lng: s.originLng },
              { lat: dest.lat, lng: dest.lng },
            )
          : null;
      void updateWmhLiveActivity({
        percentComplete: remaining === null ? 0 : 100 - remaining,
        etaMinutes: dynamicEta,
        status: isOverdueRef.current ? "overdue" : "active",
      });
    };
    notifIntervalRef.current = setInterval(refresh, NOTIFICATION_REFRESH_MS);
    return () => {
      if (notifIntervalRef.current) {
        clearInterval(notifIntervalRef.current);
        notifIntervalRef.current = null;
      }
    };
  }, [state.active, state.destination]);

  // Auto-arrival (P0-17a / P0-17c). Runs whenever the walker's location
  // updates; when they cross inside the 50m destination geofence, mark
  // arrived through the same path as the manual button. Dedupe is via
  // state.active — endWalk flips it false before the next tick.
  useEffect(() => {
    if (!state.active || !state.destination) return;
    if (
      isWithinArrival(
        state.currentLat,
        state.currentLng,
        state.destination.lat,
        state.destination.lng,
      )
    ) {
      void markArrivedRef.current();
    }
  }, [state.active, state.currentLat, state.currentLng, state.destination]);

  // Countdown timer
  useEffect(() => {
    if (!state.active || !state.startTime || !state.etaMinutes) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    const totalMs = (state.etaMinutes + BUFFER_MINUTES) * 60 * 1000;
    const deadline = state.startTime + totalMs;

    timerRef.current = setInterval(() => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((deadline - now) / 1000));
      setRemainingSeconds(diff);

      if (diff <= 0) {
        setIsOverdue(true);
        // Auto-alert
        if (!alertSent) {
          setAlertSent(true);
          triggerOverdueAlert();
        }
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state.active, state.startTime, state.etaMinutes, alertSent]);

  const triggerOverdueAlert = () => {
    // NOTE (#230): the SMS escalation for an overdue walk is NOT done here — it
    // is handled server-side by the scheduled `walkMeHomeDelayAlert` Cloud
    // Function (functions/src/walkMeHomeDelayAlert.ts), which runs every minute,
    // scans walkMeHome/{sessionId} for sessions still `active` and >=5 min past
    // `expectedArrivalMs`, and texts each watcher via Twilio (idempotent via
    // `delayAlertSentAt`). That's deliberate: the server detector covers the
    // case where the walker's own phone is offline, which this client path
    // can't. So this client path is only a best-effort LOCAL notification +
    // the `overdue:true` flag — do NOT add a client-side watcher SMS here (it
    // would double-text and race the function's idempotency guard). Delivery of
    // the server SMS is gated on A2P 10DLC approval (pending), same as all other
    // outbound SMS.
    // Send notification via Firebase / show browser notification
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("⚠️ Walk Me Home Alert", {
        body: `${user?.displayName || "User"} hasn't marked safe! Check on them.`,
        icon: "🚨",
      });
    }
    // Update Firebase session as overdue. Safety-critical write (marks the
    // walker overdue) that was an empty `catch {}` — and because `set` wasn't
    // awaited, even that catch never saw an async rejection. Attach a
    // .catch/try so BOTH a sync throw and an async rejection are captured. The
    // write is still fire-and-forget (logging-only — no control-flow change).
    const sid = state.sessionId;
    if (sid) {
      try {
        const db = getDatabase();
        void set(ref(db, `walkMeHome/${sid}/overdue`), true).catch((e) => {
          recordRtdbWriteFailed(wmhSink, e, {
            system: "A",
            phase: "overdue_flag",
            sessionId: sid,
          });
        });
      } catch (e) {
        recordRtdbWriteFailed(wmhSink, e, {
          system: "A",
          phase: "overdue_flag",
          sessionId: sid,
        });
      }
    }
  };

  // End walk. `kind` controls the RTDB status write + whether we fire the
  // arrived SMS to watchers. Note: the local overdue/countdown teardown
  // (setRemainingSeconds/setIsOverdue/setAlertSent) is unchanged from
  // pre-11b — Option 1 leaves main's pill-goes-red path alone.
  const endWalk = useCallback(
    async (kind: "arrived" | "cancelled") => {
      const s = stateRef.current;
      if (!s.active) return;

      if (locationWatchRef.current) {
        locationWatchRef.current.clear();
        locationWatchRef.current = null;
      }
      // Drop the lock-screen notification immediately so the user doesn't
      // see a stale "Walk Me Home" card after they've marked safe.
      void hideEtaNotification();
      // End the Live Activity with the outcome (arrived/cancelled) so its final
      // lock-screen state is correct before it dismisses. iOS-only no-op elsewhere.
      void endWmhLiveActivity(kind);
      // Stop listening for push-token rotations for this ended session.
      if (pushTokenListenerRef.current) {
        pushTokenListenerRef.current.remove();
        pushTokenListenerRef.current = null;
      }
      logUserAction("walk-me-home-stopped", {
        sessionId: s.sessionId ?? "unknown",
        kind,
      });

      const sessionId = s.sessionId;
      const dest = s.destination;
      const watchers = s.watchers;
      const now = Date.now();

      // Write the new status BEFORE removing — the future Phase 11d scheduled
      // function uses status === "active" as its trigger guard, so flipping
      // it first prevents a race where a delay alert fires for a session
      // that's just ended.
      if (sessionId) {
        try {
          const db = getDatabase();
          const updateRef = ref(db, `walkMeHome/${sessionId}`);
          const fields: Record<string, unknown> = {
            status: kind,
            active: false,
            updatedAt: now,
          };
          if (kind === "arrived") fields.arrivedAt = now;
          if (kind === "cancelled") fields.cancelledAt = now;
          await update(updateRef, fields);
        } catch (e) {
          console.warn("[walkMeHome] status update failed:", e);
          // This write guards the future delay-alert function — a silent
          // failure could let a delay alert fire for an already-ended session.
          recordRtdbWriteFailed(wmhSink, e, {
            system: "A",
            phase: "status_update",
            sessionId,
          });
        }
      }

      // Backend SMS fan-out (Batch D P0-15d) replaces the native sms: bounce.
      // Only fires for the "arrived" path — cancellations are silent so the
      // walker can back out without spamming their watchers. Surface a toast
      // to the walker on failure (Question C — TWILIO env vars unset or any
      // non-ok response means the safety net didn't activate).
      if (kind === "arrived" && watchers.length > 0 && dest && sessionId) {
        const walkerName = user?.displayName || user?.email || "Someone";
        const body = buildMarkSafeSms({
          walkerName,
          destinationName: dest.name,
        });
        try {
          const outcome = await sendWalkMeHomeSms({
            to: watchers.map((w) => w.phone),
            body,
            getIdToken: () => user?.getIdToken() ?? Promise.resolve(null),
          });
          Sentry.addBreadcrumb({
            category: "walk-me-home",
            message: "arrived_sms_outcome",
            level: outcome.ok ? "info" : "warning",
            data: {
              sessionId,
              sent: outcome.sent,
              failed: outcome.failed,
              summary: summarizeSmsOutcome(outcome),
            },
          });
          if (!outcome.ok) {
            toast.error(
              `Couldn't text your contacts that you arrived (${outcome.sent}/${outcome.sent + outcome.failed} sent).`,
            );
          }
        } catch (err) {
          console.error("[walkMeHome] arrived SMS failed:", err);
          Sentry.captureException(err, {
            tags: { area: "walk-me-home", phase: "arrived-sms" },
          });
          toast.error("Couldn't text your contacts that you arrived.");
        }
      }

      // Finally remove from RTDB. Deliberately remove rather than leave an
      // "arrived" tombstone — the watcher's TrackingPage handles missing
      // sessions gracefully (existing main behavior).
      if (sessionId) {
        try {
          const db = getDatabase();
          await remove(ref(db, `walkMeHome/${sessionId}`));
        } catch (e) {
          // Was an empty catch {}. A failed remove leaves a stale "live"
          // session in RTDB (watchers keep seeing it as active). Capture it.
          recordRtdbWriteFailed(wmhSink, e, {
            system: "A",
            phase: "session_remove",
            sessionId,
          });
        }
      }

      setState({
        active: false,
        destination: null,
        startTime: null,
        etaMinutes: null,
        expectedArrivalMs: null,
        sessionId: null,
        currentLat: 0,
        currentLng: 0,
        watchers: [],
        travelMode: DEFAULT_TRAVEL_MODE,
        originLat: 0,
        originLng: 0,
      });
      setRemainingSeconds(0);
      setIsOverdue(false);
      setAlertSent(false);
    },
    [user],
  );

  const markSafe = useCallback(async () => {
    await endWalk("arrived");
  }, [endWalk]);

  const cancelWalk = useCallback(() => {
    // Fire-and-forget — user is mid-tap; we don't want to block UI.
    void endWalk("cancelled");
  }, [endWalk]);

  // Auto-arrival shares the markSafe path so watchers get the same SMS.
  // Held on a ref so the geofence useEffect can call it without binding
  // to markSafe's identity (which re-binds every render).
  markArrivedRef.current = async () => {
    logUserAction("walk-me-home-auto-arrived", {
      sessionId: stateRef.current.sessionId ?? "unknown",
    });
    await endWalk("arrived");
  };

  const shareLink = state.sessionId ? buildTrackLink(state.sessionId) : null;

  return (
    <WalkMeHomeContext.Provider
      value={{
        ...state,
        remainingSeconds,
        isOverdue,
        shareLink,
        startWalk,
        markSafe,
        cancelWalk,
      }}
    >
      {children}
    </WalkMeHomeContext.Provider>
  );
}
